import { useState, useMemo } from "react";
import { useReportSendLog } from "../hooks/useReportSendLog";
import { useLogoStore } from "../hooks/useLogoStore";
import { buildSnapshotReportHtml, EMPTY_SNAPSHOT } from "../utils/buildSnapshotReport";

// Per-client report send history. No documents are stored anywhere — the
// "view report" button REGENERATES the report from that month's holdings
// snapshot (which keeps the full fund objects), mirroring the fundsForView
// mapping in ClientDashboard. Zero DB weight.
export default function ClientReportHistoryModal({ clientId, clientName, snapshots, liveFunds, onClose }) {
  const { log } = useReportSendLog();
  const { logo } = useLogoStore();
  const [busyIdx, setBusyIdx] = useState(null);
  const [error, setError] = useState("");

  const entry = log[clientId];
  const history = useMemo(() => {
    const hist = entry?.history || [];
    if (hist.length) return hist;
    // legacy record from before history existed — show the last send
    if (entry?.lastSentAt) {
      return [{ sentAt: entry.lastSentAt, email: entry.email, kind: "bulk", subject: "", period: entry.lastSentAt.slice(0, 7) }];
    }
    return [];
  }, [entry]);

  const fmtDateTime = (iso) => iso
    ? `${new Date(iso).toLocaleDateString("he-IL")} ${new Date(iso).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" })}`
    : "—";

  // Rebuild the funds array for a report month from the holdings snapshot,
  // mirroring ClientDashboard's fundsForView mapping (redeem ← balance, etc.).
  const fundsForPeriod = (period) => {
    const snap = (snapshots || []).find(s => s.period === period);
    if (!snap?.perFund?.length) return { funds: null, fromSnapshot: false };
    const validDate = snap.valueDate ? snap.valueDate.split("-").reverse().join("/") : undefined;
    const funds = snap.perFund.map(pf => ({
      ...pf,
      redeem: pf.balance,
      validDate: validDate || pf.validDate,
      dmeiNihul: {
        ...pf.dmeiNihul,
        actual: {
          ...(pf.dmeiNihul?.actual || {}),
          dmeiTzvPct: pf.dmeiNihulTzv ?? pf.dmeiNihul?.actual?.dmeiTzvPct,
          dmeiHafPct: pf.dmeiNihulHaf ?? pf.dmeiNihul?.actual?.dmeiHafPct,
        },
      },
    }));
    return { funds, fromSnapshot: true };
  };

  const viewReport = async (h, idx) => {
    setBusyIdx(idx);
    setError("");
    try {
      const period = h.period || (h.sentAt || "").slice(0, 7);
      let { funds, fromSnapshot } = fundsForPeriod(period);
      if (!funds) { funds = liveFunds || []; fromSnapshot = false; }
      if (!funds.length) throw new Error("אין נתונים לשחזור הדוח");

      const html = buildSnapshotReportHtml({
        funds, clientName, logo,
        snapshot: EMPTY_SNAPSHOT, hideRecommendations: true,
      });
      const resp = await fetch("/api/preview-pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ html }),
      });
      if (!resp.ok) {
        const json = await resp.json().catch(() => ({}));
        throw new Error(json.error || "שגיאה ביצירת PDF");
      }
      const blob = await resp.blob();
      window.open(URL.createObjectURL(blob), "_blank");
      if (!fromSnapshot) {
        setError("לא נמצאו נתוני חודש שמורים לשליחה זו — הדוח שוחזר מהנתונים הנוכחיים.");
      }
    } catch (err) {
      setError(err.message || "שגיאה בשחזור הדוח");
    } finally {
      setBusyIdx(null);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)",
      zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, direction: "rtl",
    }} onClick={onClose}>
      <div style={{ background: "white", width: "100%", maxWidth: 640, borderRadius: 16, overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "85vh" }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ background: "linear-gradient(135deg, #1e3a5f, #2563eb)", padding: "14px 20px", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800 }}>🕐 דוחות שנשלחו — {clientName}</div>
            <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>הדוחות משוחזרים מנתוני החודש השמורים</div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)", color: "white", width: 30, height: 30, borderRadius: 8, cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ padding: 18, overflowY: "auto" }}>
          {error && (
            <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#92400e", marginBottom: 12 }}>
              {error}
            </div>
          )}

          {history.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
              עוד לא נשלחו דוחות ללקוח זה.
            </div>
          ) : (
            <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f1f5f9" }}>
                    <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: "#475569" }}>תאריך ושעה</th>
                    <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: "#475569" }}>מייל</th>
                    <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700, color: "#475569", width: 66 }}>סוג</th>
                    <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700, color: "#475569", width: 110 }}>דוח</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={`${h.sentAt}_${i}`} style={{ borderTop: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "7px 10px", color: "#475569", whiteSpace: "nowrap" }} title={h.subject || ""}>
                        {fmtDateTime(h.sentAt)}
                      </td>
                      <td style={{ padding: "7px 10px", direction: "ltr", textAlign: "right", color: "#475569" }}>{h.email}</td>
                      <td style={{ padding: "7px 10px", textAlign: "center" }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, borderRadius: 8, padding: "2px 8px",
                          background: h.kind === "single" ? "#ede9fe" : "#cffafe",
                          color: h.kind === "single" ? "#7c3aed" : "#0e7490",
                        }}>
                          {h.kind === "single" ? "בודדת" : "מרוכזת"}
                        </span>
                      </td>
                      <td style={{ padding: "7px 10px", textAlign: "center" }}>
                        <button
                          onClick={() => viewReport(h, i)}
                          disabled={busyIdx !== null}
                          style={{
                            border: "1px solid #93c5fd", background: busyIdx === i ? "#eff6ff" : "white",
                            color: "#1d4ed8", borderRadius: 6, cursor: busyIdx !== null ? "default" : "pointer",
                            padding: "4px 10px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
                          }}>
                          {busyIdx === i ? "⏳ מייצר..." : "📄 צפה בדוח"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
